import {
    RequestService,
    RequestServiceResponse,
} from '@tcc-online/request-service';
import ProjectParticipant from '../types/project/ProjectParticipant';
import type { CriterionValuePair } from '../types/project/ProjectParticipant';
import AdminProjectTypes from '../constants/AdminProjectTypes';
import { isSuccessfulResponse } from '../../helpers';
import ImportService from './ImportService';
import type { ParticipantImportPreviewResponse } from '../types/ParticipantImportPreviewResponse';
import SortParams from '../types/SortParams';

const BASE_URL = '/admin/products';

const createUrl = (productId: number, projectId: number) =>
    `${BASE_URL}/${productId}/projects/${projectId}/participants`;

const AdminParticipantService = {
    getParticipants,
    addParticipant,
    deleteParticipant,
    batchDeleteParticipants,
    deleteAllParticipants,
    updateCriterionValues,
    importParticipants,
    exportParticipants,
    getImportTemplate,
};

export interface GetParticipantsPayload {
    productId: number;
    projectId: number;
    limit?: number;
    page?: number;
}

async function getParticipants(
    payload: GetParticipantsPayload & SortParams & { search?: string },
): Promise<
    RequestServiceResponse<ProjectParticipant[]> & {
        pagination?: unknown;
    }
> {
    const {
        productId,
        projectId,
        limit = 10,
        page = 1,
        sortBy,
        sortOrder,
        search,
    } = payload;
    return await RequestService.get<ProjectParticipant[]>(
        `${createUrl(productId, projectId)}`,
        { limit, page, sortBy, sortOrder, search },
    );
}

export interface AddParticipantPayload {
    productId: number;
    projectId: number;
    id?: number;
    email?: string;
    firstName: string;
    lastName: string;
    userIdentifier?: string;
    criterionValues?: CriterionValuePair[];
}

async function addParticipant(
    payload: AddParticipantPayload,
): Promise<RequestServiceResponse<ProjectParticipant>> {
    const {
        productId,
        projectId,
        id,
        email,
        firstName,
        lastName,
        userIdentifier,
        criterionValues,
    } = payload;

    const body: Record<string, unknown> = {
        id,
        email,
        firstName,
        lastName,
        userIdentifier,
    };

    if (productId === AdminProjectTypes.Survey && criterionValues) {
        body.criterionValues = criterionValues;
    }

    return await RequestService.post<
        Record<string, unknown>,
        ProjectParticipant
    >(createUrl(productId, projectId), body);
}

export interface DeleteParticipantPayload {
    productId: number;
    projectId: number;
    participantId: number;
}

async function deleteParticipant(
    payload: DeleteParticipantPayload,
): Promise<RequestServiceResponse<unknown>> {
    const { productId, projectId, participantId } = payload;
    return await RequestService.delete(
        `${createUrl(productId, projectId)}/${participantId}`,
    );
}

export interface BatchDeleteParticipantsPayload {
    productId: number;
    projectId: number;
    ids: number[];
}

async function batchDeleteParticipants(
    payload: BatchDeleteParticipantsPayload,
): Promise<RequestServiceResponse<unknown>> {
    const { productId, projectId, ids } = payload;
    return await RequestService.post<{ ids: number[] }, unknown>(
        `${createUrl(productId, projectId)}/batch-delete`,
        { ids },
    );
}

export interface DeleteAllParticipantsPayload {
    productId: number;
    projectId: number;
}

async function deleteAllParticipants(
    payload: DeleteAllParticipantsPayload,
): Promise<RequestServiceResponse<unknown>> {
    const { productId, projectId } = payload;
    return await RequestService.delete(createUrl(productId, projectId));
}

export interface UpdateCriterionValuesPayload {
    productId: number;
    projectId: number;
    participantId: number;
    criterionValues: CriterionValuePair[];
}

async function updateCriterionValues(
    payload: UpdateCriterionValuesPayload,
): Promise<RequestServiceResponse<ProjectParticipant>> {
    const { productId, projectId, participantId, criterionValues } = payload;
    return await RequestService.patch<
        { criterionValues: CriterionValuePair[] },
        ProjectParticipant
    >(`${createUrl(productId, projectId)}/${participantId}/criterion-values`, {
        criterionValues,
    });
}

export interface ImportParticipantsPayload {
    productType: number;
    projectId: number;
    data: FormData;
}

export interface ImportParticipantsResponse {
    importId: number;
    preview: ParticipantImportPreviewResponse;
}

async function importParticipants(
    payload: ImportParticipantsPayload,
): Promise<RequestServiceResponse<ImportParticipantsResponse>> {
    const { productType, projectId, data } = payload;
    const result = await RequestService.post<
        FormData,
        ImportParticipantsResponse
    >(`${createUrl(productType, projectId)}/import`, data, {
        headers: { 'Content-Type': 'multipart/form-data' },
    });

    if (isSuccessfulResponse(result.status) && result.data) {
        ImportService.saveImportId(result.data.importId);
    }

    return result;
}

export interface GetImportTemplatePayload {
    productType: number;
    projectId: number;
}

async function getImportTemplate(
    payload: GetImportTemplatePayload,
): Promise<RequestServiceResponse<Blob>> {
    const { productType, projectId } = payload;
    return await RequestService.get<Blob>(
        `${createUrl(productType, projectId)}/import/template`,
        {},
        { responseType: 'blob' },
    );
}

export interface ExportParticipantsPayload {
    productType: number;
    projectId: number;
}

async function exportParticipants(
    payload: ExportParticipantsPayload,
): Promise<RequestServiceResponse<Blob>> {
    const { productType, projectId } = payload;
    return await RequestService.get<Blob>(
        `${createUrl(productType, projectId)}/export`,
        {},
        { responseType: 'blob' },
    );
}

export default AdminParticipantService;
